import { z, type ZodType } from 'zod/v4';

import {
  drawResponseSchema,
  historyResponseSchema,
  interpretResponseSchema,
  llmHealthSchema,
  llmModelSchema,
  memorySummarySchema,
  REST_ROUTES,
  settingsResponseSchema,
  type DrawRequest,
  type DrawResponse,
  type HistoryResponse,
  type InterpretRequest,
  type InterpretResponse,
  type LLMHealth,
  type LLMModel,
  type MemorySummary,
  type ReadingRecord,
  type SettingsResponse,
  type UserPreferences,
  type UserSession,
  userSessionSchema,
} from '@lilith-arcana-os/domain';

import { appEnv } from './env';

const savedHistorySchema = z.object({
  id: z.number().int().nonnegative(),
  saved: z.boolean(),
});

interface RequestOptions<TSchema> {
  method?: 'GET' | 'POST';
  body?: unknown;
  schema: ZodType<TSchema>;
}

const request = async <TSchema>(path: string, options: RequestOptions<TSchema>): Promise<TSchema> => {
  const requestInit: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': appEnv.nonce,
    },
  };

  if (options.body !== undefined) {
    requestInit.body = JSON.stringify(options.body);
  }

  const response = await fetch(`${appEnv.restBase}${path}`, requestInit);

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `REST request failed for ${path}`);
  }

  const json = (await response.json()) as unknown;
  return options.schema.parse(json);
};

export const apiClient = {
  session: (): Promise<UserSession> =>
    request(REST_ROUTES.session, { schema: userSessionSchema, method: 'GET' }),
  draw: (payload: DrawRequest): Promise<DrawResponse> =>
    request(REST_ROUTES.draw, {
      method: 'POST',
      body: payload,
      schema: drawResponseSchema,
    }),
  interpret: (payload: InterpretRequest): Promise<InterpretResponse> =>
    request(REST_ROUTES.interpret, {
      method: 'POST',
      body: payload,
      schema: interpretResponseSchema,
    }),
  history: (page = 1, limit = 10): Promise<HistoryResponse> =>
    request(`${REST_ROUTES.history}?page=${page}&limit=${limit}`, {
      method: 'GET',
      schema: historyResponseSchema,
    }),
  saveHistory: (payload: unknown) =>
    request(REST_ROUTES.historySave, {
      method: 'POST',
      body: payload,
      schema: savedHistorySchema,
    }),
  memory: (): Promise<MemorySummary> =>
    request(REST_ROUTES.memory, {
      method: 'GET',
      schema: memorySummarySchema,
    }),
  settings: (): Promise<SettingsResponse> =>
    request(REST_ROUTES.settings, {
      method: 'GET',
      schema: settingsResponseSchema,
    }),
  saveSettings: (payload: UserPreferences): Promise<SettingsResponse> =>
    request(REST_ROUTES.settings, {
      method: 'POST',
      body: payload,
      schema: settingsResponseSchema,
    }),
  llmHealth: (): Promise<LLMHealth> =>
    request(REST_ROUTES.llmHealth, {
      method: 'POST',
      body: {},
      schema: llmHealthSchema,
    }),
  llmModels: (): Promise<LLMModel[]> =>
    request(REST_ROUTES.llmModels, {
      method: 'GET',
      schema: z.array(llmModelSchema),
    }),
  saveReadingRecord: (record: ReadingRecord) => apiClient.saveHistory(record),
};
